import type { ColorSample, RoiRect } from '../../types';

const PALETTE_NAMES = [
  { name: 'ASPHALT BLACK', value: [25, 25, 24] },
  { name: 'STEEL SHADOW', value: [86, 88, 86] },
  { name: 'URBAN DUST', value: [137, 132, 120] },
  { name: 'CONCRETE GREY', value: [174, 175, 170] },
  { name: 'SIGNAL ORANGE', value: [232, 90, 28] },
] as const;

const toHex = (value: number) => value.toString(16).padStart(2, '0').toUpperCase();
const distance = (a: readonly number[], b: readonly number[]) => Math.sqrt(a.reduce((sum, channel, index) => sum + (channel - b[index]) ** 2, 0));

function colorName(rgb: number[]) {
  return PALETTE_NAMES.reduce((best, entry) => distance(rgb, entry.value) < distance(rgb, best.value) ? entry : best).name;
}

export function sampleVideoPalette(video: HTMLVideoElement, roi?: RoiRect): ColorSample[] {
  if (!video.videoWidth || !video.videoHeight) return [];
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) return [];
  const source = roi ?? { x: 0, y: 0, width: 1, height: 1 };
  context.drawImage(video, source.x * video.videoWidth, source.y * video.videoHeight, source.width * video.videoWidth, source.height * video.videoHeight, 0, 0, 64, 64);
  const data = context.getImageData(0, 0, 64, 64).data;
  const buckets = new Map<string, { count: number; r: number; g: number; b: number }>();
  for (let index = 0; index < data.length; index += 16) {
    const r = Math.round(data[index] / 40) * 40;
    const g = Math.round(data[index + 1] / 40) * 40;
    const b = Math.round(data[index + 2] / 40) * 40;
    const key = `${r},${g},${b}`;
    const bucket = buckets.get(key) ?? { count: 0, r, g, b };
    bucket.count += 1;
    buckets.set(key, bucket);
  }
  const sorted = [...buckets.values()].sort((a, b) => b.count - a.count).slice(0, 5);
  const total = sorted.reduce((sum, entry) => sum + entry.count, 0) || 1;
  return sorted.map((entry) => ({
    hex: `#${toHex(Math.min(255, entry.r))}${toHex(Math.min(255, entry.g))}${toHex(Math.min(255, entry.b))}`,
    ratio: entry.count / total,
    name: colorName([entry.r, entry.g, entry.b]),
  }));
}

export function paletteName(palette: ColorSample[]) {
  return palette[0]?.name ?? 'STEEL SHADOW';
}
